import { getApiUrl } from '@/lib/api-config';

/** A parsed PDF: the server-side text extraction, nothing interpreted yet (the AI reads the vendor, total, items). */
export interface ParsedPdf {
  fileName: string;
  text: string;
}

interface PdfParseResponse {
  success: boolean;
  filename: string;
  data: { fullText: string };
}

async function readParsedPdf(response: Response): Promise<ParsedPdf> {
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.error || `The PDF could not be read (${response.status})`);
  }
  const result: PdfParseResponse = await response.json();
  const text = result.data?.fullText?.trim() ?? '';
  if (!result.success || !text) {
    throw new Error('No readable text was found in this PDF (is it a scanned image?)');
  }
  return { fileName: result.filename.replace(/\.pdf$/i, ''), text };
}

/**
 * PDF text extraction (backend: POST /api/pdf-parse, authenticated -- getApiUrl() makes the global
 * authenticated-fetch interceptor attach the session).
 */
export const pdfAPIService = {
  async parsePDF(file: File): Promise<ParsedPdf> {
    const formData = new FormData();
    formData.append('pdf', file);
    return readParsedPdf(await fetch(getApiUrl('api/pdf-parse'), { method: 'POST', body: formData }));
  },

  /** An already-uploaded PDF (a saved bid's attachment), by its Storage object path -- the server checks it is the caller's. */
  async parsePDFFromStorage(storagePath: string): Promise<ParsedPdf> {
    return readParsedPdf(await fetch(getApiUrl('api/pdf-parse-url'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ filePath: storagePath }),
    }));
  },
};
